/** Códigos retornados pelo LicenseGuard / License Platform */
export const LICENSE_PLATFORM_REASONS = [
  'missing_license_key',
  'domain_mismatch',
  'not_found',
  'suspended',
  'draft',
  'expired',
  'unknown',
]

/** Códigos adicionais gerados localmente pelo DragonPanel (LicenseGuard / LicenseValidator) */
export const LICENSE_LOCAL_REASONS = [
  'grace_period_expired',
  'connection_error',
  'validation_failed',
  'validation_required',
  'license_not_configured',
  'critical_error',
  'invalid',
  'active',
]

export const LICENSE_STATUS_CODES = [
  ...LICENSE_PLATFORM_REASONS,
  ...LICENSE_LOCAL_REASONS,
]

const MESSAGE_HINTS = [
  { code: 'grace_period_expired', patterns: [/offline/i, /reconect/i, /grace period/i] },
  { code: 'connection_error', patterns: [/conectar/i, /connect/i, /servidor de licen/i] },
  { code: 'expired', patterns: [/expir/i, /venc/i] },
  { code: 'domain_mismatch', patterns: [/dom[ií]nio/i, /domain/i, /autoriz/i] },
  { code: 'suspended', patterns: [/suspend/i] },
  { code: 'not_found', patterns: [/not found/i, /n[aã]o encontr/i] },
  { code: 'missing_license_key', patterns: [/license_key/i, /chave/i] },
  { code: 'critical_error', patterns: [/cr[ií]tico/i, /critical/i] },
  { code: 'validation_failed', patterns: [/validar licen/i] },
]

export function normalizeLicenseCode(rawCode, errorMessage = '') {
  const code = String(rawCode || '').trim().toLowerCase()
  if (code && LICENSE_STATUS_CODES.includes(code)) {
    return code
  }
  if (code) {
    return code
  }

  const text = String(errorMessage || '').toLowerCase()
  for (const hint of MESSAGE_HINTS) {
    if (hint.patterns.some((pattern) => pattern.test(text))) {
      return hint.code
    }
  }

  return 'invalid'
}

export function getLicenseStatusVisual(code) {
  switch (code) {
    case 'expired':
    case 'grace_period_expired':
      return {
        header: 'from-amber-500 to-orange-600',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        dot: 'bg-amber-500',
        icon: 'clock',
      }
    case 'suspended':
    case 'draft':
      return {
        header: 'from-neutral-600 to-neutral-800',
        badge: 'bg-neutral-200 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200',
        dot: 'bg-neutral-500',
        icon: 'pause',
      }
    case 'domain_mismatch':
      return {
        header: 'from-violet-600 to-primary-600',
        badge: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
        dot: 'bg-violet-500',
        icon: 'globe',
      }
    case 'connection_error':
    case 'validation_failed':
    case 'critical_error':
      return {
        header: 'from-rose-600 to-danger-600',
        badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
        dot: 'bg-rose-500',
        icon: 'alert',
      }
    case 'not_found':
    case 'missing_license_key':
      return {
        header: 'from-primary-600 to-purple-600',
        badge: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300',
        dot: 'bg-primary-500',
        icon: 'key',
      }
    case 'active':
      return {
        header: 'from-emerald-500 to-teal-600',
        badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
        dot: 'bg-emerald-500',
        icon: 'shield',
      }
    default:
      return {
        header: 'from-danger-500 to-rose-600',
        badge: 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-300',
        dot: 'bg-danger-500',
        icon: 'shield',
      }
  }
}

export function resolveLicensePresentation({ code, errorMessage, t, panelName }) {
  const normalized = normalizeLicenseCode(code, errorMessage)
  const i18nKey = `license.status.${normalized}`
  const hasDedicatedCopy = typeof t === 'function' && t(i18nKey + '.title') !== i18nKey + '.title'

  const title = hasDedicatedCopy
    ? t(`${i18nKey}.title`)
    : t('license.invalid.default_error_title')

  const message = hasDedicatedCopy
    ? t(`${i18nKey}.message`, { panelName })
    : (errorMessage || t('license.invalid.default_error_message', { panelName }))

  const badge = hasDedicatedCopy
    ? t(`${i18nKey}.badge`)
    : t('license.invalid.status_invalid')

  const hint = hasDedicatedCopy && t(`${i18nKey}.hint`) !== `${i18nKey}.hint`
    ? t(`${i18nKey}.hint`)
    : ''

  return {
    code: normalized,
    title,
    message,
    badge,
    hint,
    visual: getLicenseStatusVisual(normalized),
  }
}
